import { useEffect, useState } from 'react'
import { downloadURL } from '../lib/firebase'
import { track } from '../lib/analytics'

/**
 * Page de retour après paiement.
 *
 * La clé vient du webhook Stripe, qui peut arriver après la redirection du
 * navigateur : l'API répond alors 202 et on réinterroge. Afficher une erreur
 * dans cet intervalle ferait paniquer quelqu'un qui vient de payer.
 */
export default function License() {
interface SessionState {
  status: 'loading' | 'ready' | 'error'
  key?: string
  email?: string
  seats?: number
  message?: string
}

  const [state, setState] = useState<SessionState>({ status: 'loading' })

  useEffect(() => {
    const sessionId: string | null = new URLSearchParams(window.location.search).get('session_id')
    if (sessionId === null) {
      setState({ status: 'error', message: 'No payment reference in the link.' })
      return
    }

    let cancelled = false
    let attempts = 0

    const id: string = sessionId

    async function poll() {
      attempts += 1
      try {
        const res = await fetch(`/api/license/session?session_id=${encodeURIComponent(id)}`)
        if (cancelled) return

        if (res.status === 200) {
          track('purchase')
          setState({ status: 'ready', ...(await res.json()) })
          return
        }
        if (res.status === 202 && attempts < 20) {
          setTimeout(poll, 1500)
          return
        }
        setState({
          status: 'error',
          message: 'Your payment went through, but the licence is taking longer than usual. '
            + 'It will appear in your account area, and we have it on file — contact us if not.',
        })
      } catch {
        if (!cancelled && attempts < 20) setTimeout(poll, 1500)
      }
    }

    poll()
    return () => { cancelled = true }
  }, [])

  return (
    <main className="shell account">
      <div className="card">
        {state.status === 'loading' && (
          <>
            <h2 style={{ marginTop: 0 }}>Finalising your purchase…</h2>
            <p style={{ color: 'var(--muted)' }}>This takes a few seconds. Do not close this page.</p>
          </>
        )}

        {state.status === 'error' && (
          <>
            <h2 style={{ marginTop: 0 }}>Almost there</h2>
            <p className="alert">{state.message}</p>
          </>
        )}

        {state.status === 'ready' && (
          <>
            <h2 style={{ marginTop: 0 }}>Thank you — here is your licence key</h2>
            <p style={{
              fontSize: '1.5rem', fontWeight: 700, letterSpacing: '.1em',
              textAlign: 'center', margin: '1.5rem 0', userSelect: 'all',
              fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
            }}>
              {state.key}
            </p>
            <p style={{ color: 'var(--muted)' }}>
              Open AppSwitch, click the menu bar icon → <strong>AppSwitch Pro…</strong>, and paste
              the key. It works on {state.seats} Macs. Keep this key — sign in with{' '}
              <strong>{state.email}</strong> on this site to find it again at any time.
            </p>
            <a className="btn btn-primary btn-block" href={downloadURL}>Download AppSwitch</a>
          </>
        )}
      </div>
    </main>
  )
}
